// OrgChart TypeScript Interfaces

export type NodeType = 'group' | 'division' | 'company' | 'warehouse' | 'location';

export interface RegionalSettings {
  currency: string | null;
  timezone: string | null;
  language: string | null;
  date_format: string | null;
}

export interface EffectiveSettings {
  currency: { value: string; source: string };
  timezone: { value: string; source: string };
  language: { value: string; source: string };
  date_format: { value: string; source: string };
}

export interface OrgChartNode {
  id: string;                    // Prefixed ID like "group_1", "company_2"
  entity_id: number;             // Actual database ID
  type: NodeType;
  name: string;
  code?: string;
  is_active: boolean;
  children: OrgChartNode[];

  // Type-specific fields
  site_type?: string;
  warehouse_type?: string;
  location_type?: string;
  company_type?: string;  // Can be 'customer' | 'supplier' but API returns string
  portal_mode?: 'customer' | 'supplier' | 'both';
  level?: number;
  description?: string;

  // Location metadata
  metadata?: {
    // CR-6691: full address for company nodes
    address_line_1?: string;
    address_line_2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
    headquarters_country?: string;
    website?: string;
    primary_email?: string;
    manager_name?: string;
    // CR-6247: expanded company fields, so the edit dialog pre-fills without
    // a second fetch per node.
    registration_number?: string;
    year_established?: number | null;
    employee_count?: number | null;
    /** Decimal serialised as a string. */
    annual_revenue?: string | null;
  };

  // Regional settings. On a group these are the values set on that group; on a
  // site (CR-6256) they are the resolved values, since an inheriting site's own
  // columns are dormant.
  regional_settings?: RegionalSettings;

  /**
   * CR-6256: site nodes only — true when the site inherits its regional
   * settings from its group rather than holding its own override.
   */
  uses_group_regional_defaults?: boolean;

  // Computed effective settings (with inheritance resolved)
  effective_settings?: EffectiveSettings;
}

export interface OrgChartStats {
  total_groups: number;
  total_divisions: number;
  total_companies: number;
  total_warehouses: number;
  total_locations: number;
}

export interface HierarchyLabels {
  group: string;
  division: string;
  site: string;
}

export interface OrgChartData {
  tree: OrgChartNode[];
  stats: OrgChartStats;
  labels?: HierarchyLabels;
}

// Layout calculation types
export interface NodePosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LayoutNode extends OrgChartNode {
  position: NodePosition;
  depth: number;
  isExpanded: boolean;
  parentId: string | null;
}

// Node color configuration
export const NODE_COLORS: Record<NodeType, { bg: string; border: string; text: string; badge: string }> = {
  group: {
    bg: 'bg-purple-50 dark:bg-purple-950/30',
    border: 'border-purple-200 dark:border-purple-800',
    text: 'text-purple-700 dark:text-purple-300',
    badge: 'bg-purple-500',
  },
  division: {
    bg: 'bg-indigo-50 dark:bg-indigo-950/30',
    border: 'border-indigo-200 dark:border-indigo-800',
    text: 'text-indigo-700 dark:text-indigo-300',
    badge: 'bg-indigo-500',
  },
  company: {
    bg: 'bg-blue-50 dark:bg-blue-950/30',
    border: 'border-blue-200 dark:border-blue-800',
    text: 'text-blue-700 dark:text-blue-300',
    badge: 'bg-blue-500',
  },
  warehouse: {
    bg: 'bg-orange-50 dark:bg-orange-950/30',
    border: 'border-orange-200 dark:border-orange-800',
    text: 'text-orange-700 dark:text-orange-300',
    badge: 'bg-orange-500',
  },
  location: {
    bg: 'bg-gray-50 dark:bg-gray-950/30',
    border: 'border-gray-200 dark:border-gray-700',
    text: 'text-gray-700 dark:text-gray-300',
    badge: 'bg-gray-500',
  },
};

// Layout constants
export const LAYOUT_CONSTANTS = {
  NODE_WIDTH: 180,
  NODE_HEIGHT: 130,
  HORIZONTAL_GAP: 40,
  VERTICAL_GAP: 60,
  MIN_ZOOM: 0.25,
  MAX_ZOOM: 2,
  ZOOM_STEP: 0.1,
};

// Default node type labels (overridden by configurable hierarchy labels from platform settings)
export const DEFAULT_NODE_TYPE_LABELS: Record<NodeType, string> = {
  group: 'Group',
  division: 'Division',
  company: 'Company',
  warehouse: 'Warehouse',
  location: 'Location',
};

// Mutable labels that can be updated from API response
export let NODE_TYPE_LABELS: Record<NodeType, string> = { ...DEFAULT_NODE_TYPE_LABELS };

/** Apply configurable hierarchy labels from the API. */
export function applyHierarchyLabels(labels: HierarchyLabels): void {
  NODE_TYPE_LABELS = {
    ...DEFAULT_NODE_TYPE_LABELS,
    group: labels.group,
    division: labels.division,
    company: labels.site || 'Company',
  };
}

// Allowed child types for each node type
export const ALLOWED_CHILDREN: Record<NodeType, NodeType[]> = {
  group: ['division', 'company'],  // Groups can have divisions or direct companies
  division: ['company'],           // Divisions contain companies
  company: [],                     // Companies are leaf nodes (operational units)
  warehouse: [],                   // Reserved for future use
  location: [],                    // Reserved for future use
};
